import React from 'react'
import {
  Clock,
  Percent,
  Timer,
  Users,
  UserCheck,
} from 'lucide-react'
import type { OrgEmployeeDisplayKpis } from '../../../types/employeeAnalysis'

export interface EmployeeOrgKpiStripProps {
  display: OrgEmployeeDisplayKpis | null
  loading?: boolean
}

export function EmployeeOrgKpiStrip({
  display,
  loading,
}: EmployeeOrgKpiStripProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 animate-pulse">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-xl border border-slate-200 h-24"
          />
        ))}
      </div>
    )
  }

  if (!display) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
        Organization stats could not be loaded. Generate{' '}
        <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">
          organization_stats.json
        </code>{' '}
        if needed.
      </div>
    )
  }

  const tiles = [
    {
      label: 'Total employees',
      value: display.totalUsers.toLocaleString(),
      sub: display.subtitle || 'With timesheet data',
      accent: 'bg-blue-500',
      textColor: 'text-blue-700',
      bg: 'bg-blue-50',
      icon: <Users size={16} className="text-blue-500" />,
    },
    {
      label: 'Total hours',
      value: display.totalHours.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      }),
      sub: display.subtitle || 'Period',
      accent: 'bg-violet-500',
      textColor: 'text-violet-700',
      bg: 'bg-violet-50',
      icon: <Clock size={16} className="text-violet-500" />,
    },
    {
      label: 'Billable hours',
      value: display.billableHours.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      }),
      sub: 'Client-chargeable',
      accent: 'bg-emerald-500',
      textColor: 'text-emerald-700',
      bg: 'bg-emerald-50',
      icon: <Timer size={16} className="text-emerald-500" />,
    },
    {
      label: 'Avg utilization',
      value: `${display.avgUtilization.toFixed(1)}%`,
      sub: 'Billable / total',
      accent: 'bg-orange-500',
      textColor: 'text-orange-700',
      bg: 'bg-orange-50',
      icon: <UserCheck size={16} className="text-orange-500" />,
    },
    {
      label: 'Late submission %',
      value:
        display.lateEntryRate != null
          ? `${display.lateEntryRate.toFixed(1)}%`
          : '—',
      sub: 'Late entries / total entries',
      accent: 'bg-amber-500',
      textColor: 'text-amber-700',
      bg: 'bg-amber-50',
      icon: <Percent size={16} className="text-amber-500" />,
    },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="bg-white rounded-xl border border-slate-200 shadow-md p-4 flex gap-3 items-start"
        >
          <div className={`w-1 self-stretch rounded-full flex-none ${tile.accent}`} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
              {tile.label}
            </p>
            <p
              className={`text-xl font-bold tabular-nums leading-tight ${tile.textColor}`}
            >
              {tile.value}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
              {tile.sub}
            </p>
          </div>
          <div
            className={`w-8 h-8 rounded-lg ${tile.bg} flex items-center justify-center flex-none`}
          >
            {tile.icon}
          </div>
        </div>
      ))}
    </div>
  )
}
